import { useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useToast } from "../../../components/ui";
import { fetchAccount } from "../services/accountService";
import {
  clearGivenConsent,
  fetchOutstandingConsents,
  readGivenConsent,
  recordConsent,
} from "../services/consentService";
import { createWorkspace } from "../services/onboardingService";
import { clearReferralCode, readReferralCode } from "../services/referralCode";
import { ApiError } from "../../../lib/http";
import type {
  AccountSyncOutcome,
  AuthAccount,
  BootstrapStep,
  GetToken,
} from "../types/auth.types";
import type { ConsentDocument } from "../types/consent.types";

export interface AccountSyncOptions {
  /** Called as each request starts, for the loading screen's progress bar. */
  onStep?: (step: BootstrapStep) => void;
  /**
   * Called when the backend still wants the user's agreement to something and
   * there is no answer to give on their behalf - a returning user meeting a
   * document published since they last agreed.
   *
   * The handshake stops here until the returned promise resolves, which is what
   * makes the gate a gate: nothing further is created for a user whose
   * agreement is not on record. Resolving means the backend has recorded it, so
   * whoever shows the screen owns the POST and any retry it needs.
   */
  onConsentRequired: (documents: ConsentDocument[]) => Promise<void>;
}

/**
 * Introduces a freshly activated Clerk session to our own backend: whoami, then
 * the DPDP consent the user gave on the way in, then the workspace they own.
 *
 * Every way into the app ends here - the OTP screen after `setActive`, the SSO
 * callback after the Google handshake - so both run the same calls and report
 * failures the same way.
 *
 * The returned function never rejects; it resolves to the outcome instead.
 * `RequireAuth` holds the app on a loading screen until it does, so the user
 * only reaches the dashboard once they have all answered - which is the whole
 * point of running them here rather than letting each screen fetch on arrival.
 *
 * `onStep` is called as each request starts, for that screen's progress bar.
 *
 * Must be called only once the session is active: `getToken()` has nothing to
 * hand out before that, and every endpoint is 401 without a bearer token.
 */

/**
 * Settles whatever DPDP consent the backend is still waiting for.
 *
 * The read comes first for every session, sign-up or log-in, and it is what
 * decides the rest: it names the documents outstanding and, just as important,
 * the version each acceptance has to be recorded against - so the app never
 * asserts an agreement to a version it guessed at, and never re-sends one the
 * backend already holds.
 *
 * What happens next depends on whether there is an answer to give:
 *
 *   - A sign-up parked one on the way in, before the account existed. It is
 *     posted without asking again, because it was already asked.
 *   - Anything still outstanding after that has no answer behind it - a
 *     returning user meeting a republished document - so it is put to them, and
 *     this waits.
 *   - Nothing outstanding is the ordinary case, and costs one GET.
 */
async function settleConsent(
  getToken: GetToken,
  onStart: () => void,
  onConsentRequired: AccountSyncOptions["onConsentRequired"]
): Promise<void> {
  onStart();
  const outstanding = await fetchOutstandingConsents(getToken);

  if (outstanding.length === 0) {
    // The backend already holds everything. A record still parked from a
    // sign-up was acted on in an earlier session, so it has done its job.
    clearGivenConsent();
    return;
  }

  // Anything the POST below cannot get onto the backend throws out of here, so
  // reaching the end is what "settled" means and there is nothing to track.

  const given = readGivenConsent();

  // Only what the user actually ticked. The two lists should agree, but an
  // acceptance is not something to send on the strength of "should": a document
  // they were never shown is left outstanding rather than agreed to on their
  // behalf - and so ends up on the screen below, where they can answer for it.
  const answered = given?.length
    ? outstanding.filter((doc) => given.includes(doc.documentType))
    : [];

  if (answered.length > 0) {
    await recordConsent(getToken, answered);
  }
  if (given) {
    // Only now - a failed post must leave the record in place to be retried,
    // because there is no way to recover it once it is gone.
    clearGivenConsent();
  }

  const unanswered = outstanding.filter((doc) => !answered.includes(doc));
  if (unanswered.length > 0) {
    await onConsentRequired(unanswered);
  }
}

export function useAccountSync(): (
  options: AccountSyncOptions
) => Promise<AccountSyncOutcome> {
  const { getToken } = useAuth();
  const toast = useToast();

  return useCallback(
    async ({ onStep, onConsentRequired }) => {
      let account: AuthAccount;

      onStep?.("account");
      try {
        account = await fetchAccount(getToken);
      } catch (err) {
        console.error("whoami failed:", err);
        // Returned rather than toasted: the user is still on the loading
        // screen, so this is shown there with a retry beside it.
        return {
          ok: false,
          message:
            err instanceof ApiError
              ? err.message
              : "We could not reach the server. Check your connection and try again.",
        };
      }

      // A valid session whose records the backend has not written yet. The app
      // will look empty rather than broken, so say why.
      if (!account.provisioned) {
        toast.info(
          "Finishing account setup",
          "Some of your data may take a moment to appear."
        );
      }

      // Between whoami and the workspace: the session has to exist for the
      // backend to attach the agreement to anybody, and the agreement has to be
      // on record before we start creating things on the user's behalf.
      let consentSettled = true;
      try {
        await settleConsent(
          getToken,
          () => onStep?.("consent"),
          onConsentRequired
        );
      } catch (err) {
        // Not fatal, and deliberately not a blocker. The backend's own list of
        // outstanding documents is what decides whether the user gets asked
        // again, and it is unchanged by this failing - so the honest outcome is
        // that they are asked again, not that they are locked out now.
        //
        // Reported all the same, so the handshake is not remembered as done.
        // The user has already agreed; the only thing standing between that and
        // the backend is a call that can be made again, and a session written
        // off here would never make it - the parked record would go with the
        // tab and they would be asked a second time for something they had
        // already answered.
        consentSettled = false;
        toast.error(
          "Could not save your agreements",
          err instanceof ApiError
            ? err.message
            : "You are signed in, but we could not reach the server."
        );
        console.error("consent submission failed:", err);
      }

      onStep?.("workspace");
      try {
        // Named after the user until an onboarding screen asks for a practice
        // name; the backend takes both fields, so both get the same value.
        //
        // The referral code is whatever a partner's link parked on the way in,
        // and this is the one call that can record it: the backend reads it
        // when the practice is created and at no later moment. `null` for the
        // ordinary sign-up, which is most of them.
        await createWorkspace(getToken, {
          practiceName: account.fullName,
          fullName: account.fullName,
          referralCode: readReferralCode(),
        });
        // Only on success, and regardless of whether this call was the one
        // that created the practice. A `200` means the workspace was already
        // there and the code was ignored - it will be ignored by every future
        // call too, so keeping it would leave a code parked for the rest of
        // the browser session with nothing left that could ever spend it.
        //
        // A failure deliberately leaves it where it is: the handshake runs
        // again on the next session, and a referral dropped here would be a
        // partner uncredited for a practice that did come through their link.
        clearReferralCode();
      } catch (err) {
        toast.error(
          "Could not set up your workspace",
          err instanceof ApiError
            ? err.message
            : "You are signed in, but we could not reach the server."
        );
        console.error("workspace onboarding failed:", err);
      }

      return { ok: true, consentSettled };
    },
    [getToken, toast]
  );
}
